import { useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { httpClient } from "../network";
import {
  getViberQuotaEndpoint,
  getViberQuotaHistoryEndpoint,
  viberTopupEndpoint,
  updateViberSettingsEndpoint,
  getViberMessagesEndpoint,
  getViberMessagesByPhoneEndpoint,
  sendViberMarketingEndpoint,
  sendViberMarketingWithImageEndpoint,
  getViberMessageHistoryEndpoint,
  getViberCountersEndpoint,
  updateViberServiceLimitEndpoint,
} from "../endpoints";
import { toast } from "react-toastify";

export interface ViberQuota {
  monthlyLimit: number;
  sentCount: number;
  remainingMessages: number;
  topUpCount: number;
  topUpTotal: number;
  autoTopUpEnabled: boolean;
  autoTopUpThreshold?: number;
  autoTopUpIncrement?: number;
  currentMonth: number;
  currentYear: number;
}

export interface ViberMessage {
  id: string;
  messageId: string;
  bulkId: string;
  recipientPhone: string;
  messageType: string;
  messageContent: string;
  status: string;
  label: string;
  reservationId: string | null;
  clientId: string | null;
  errorMessage: string | null;
  retryCount: number;
  sentAt: string;
  deliveredAt: string | null;
  seenAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ViberMessagesResponse {
  messages: ViberMessage[];
  stats: Record<string, number>;
}

export interface TopupRequest {
  amount: number;
  costPerMessage: number;
}

export interface UpdateSettingsRequest {
  autoTopUpEnabled: boolean;
  autoTopUpThreshold: number;
  autoTopUpIncrement: number;
}

// Get Viber quota
export const useGetViberQuota = (): UseQueryResult<ViberQuota, unknown> => {
  return useQuery({
    queryKey: ["viberQuota"],
    queryFn: async () => {
      const response: AxiosResponse<{ statusCode: number; data: ViberQuota }> = await httpClient.get(
        getViberQuotaEndpoint()
      );
      return response.data.data;
    },
    retry: 1,
  });
};

// Get Viber quota history (for month navigation)
export const useGetViberQuotaHistory = (year: number, month: number): UseQueryResult<ViberQuota, unknown> => {
  return useQuery({
    queryKey: ["viberQuotaHistory", year, month],
    queryFn: async () => {
      const response: AxiosResponse<{ statusCode: number; data: ViberQuota }> = await httpClient.get(
        getViberQuotaHistoryEndpoint(year, month)
      );
      return response.data.data;
    },
    retry: 1,
  });
};

// Manual top-up
export const useViberTopup = () => {
  return useMutation<any, Error, TopupRequest>({
    mutationFn: async (data: TopupRequest) => {
      const response: AxiosResponse<any> = await httpClient.post(
        viberTopupEndpoint(),
        data
      );
      return response.data;
    },
    onError: (error) => {
      toast.error("Greška prilikom dopune Viber poruka!");
    },
    onSuccess: (data) => {
      toast.success(
        `Uspješno dodano ${data.amountAdded} poruka. Novo stanje: ${data.newAvailableMessages}`
      );
    },
  });
};

// Update auto top-up settings
export const useUpdateViberSettings = () => {
  return useMutation<any, Error, UpdateSettingsRequest>({
    mutationFn: async (data: UpdateSettingsRequest) => {
      const response: AxiosResponse<any> = await httpClient.put(
        updateViberSettingsEndpoint(),
        data
      );
      return response.data;
    },
    onError: (error) => {
      toast.error("Greška prilikom ažuriranja postavki!");
    },
    onSuccess: () => {
      toast.success("Postavke uspješno uređene");
    },
  });
};

// Get message logs
export const useGetViberMessages = (
  skip?: number,
  limit?: number,
  messageType?: string,
  status?: string,
  dateFrom?: string,
  dateTo?: string,
  phoneNumber?: string
): UseQueryResult<ViberMessagesResponse, unknown> => {
  return useQuery({
    queryKey: [
      "viberMessages",
      skip,
      limit,
      messageType,
      status,
      dateFrom,
      dateTo,
      phoneNumber,
    ],
    queryFn: async () => {
      const response: AxiosResponse<{ statusCode: number; data: ViberMessagesResponse }> =
        await httpClient.get(
          getViberMessagesEndpoint(skip, limit, messageType, status, dateFrom, dateTo, phoneNumber)
        );
      return response.data.data;
    },
    retry: 1,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
  });
};

export interface ViberPhoneDiagnostics {
  totalSent: number;
  totalDelivered: number;
  totalSeen: number;
  totalFailed: number;
  deliveryRate: string;
  lastMessageAt: string | null;
}

export interface ViberPhoneMessagesResponse {
  phoneNumber: string;
  messages: ViberMessage[];
  diagnostics: ViberPhoneDiagnostics;
}

export interface MarketingResult {
  phoneNumber: string;
  messageId: string;
  status: string;
}

export interface SendMarketingRequest {
  message: string;
  /** Explicit recipients. Omit when sending via marketingListId. */
  phoneNumbers?: string[];
  /** Marketing list to send to; the backend resolves + consent-filters recipients. */
  marketingListId?: string;
  imageUrl?: string;
  buttonText?: string;
  buttonUrl?: string;
}

export interface SendMarketingResponse {
  sent: number;
  failed: number;
  results: MarketingResult[];
}

// Get messages by phone number (with diagnostics)
export const useGetViberMessagesByPhone = (
  phoneNumber: string
): UseQueryResult<ViberPhoneMessagesResponse, unknown> => {
  return useQuery({
    queryKey: ["viberMessagesByPhone", phoneNumber],
    queryFn: async () => {
      const response: AxiosResponse<{ statusCode: number; data: ViberPhoneMessagesResponse }> =
        await httpClient.get(getViberMessagesByPhoneEndpoint(phoneNumber));
      return response.data.data;
    },
    enabled: phoneNumber.trim().length > 0,
    retry: 1,
    staleTime: 0,
    refetchOnMount: true,
  });
};

// Send marketing/promotional messages
export const useSendViberMarketing = () => {
  return useMutation<{ data: SendMarketingResponse; message: string }, Error, SendMarketingRequest>({
    mutationFn: async (data: SendMarketingRequest) => {
      const response: AxiosResponse<{ data: SendMarketingResponse; message: string }> =
        await httpClient.post(sendViberMarketingEndpoint(), data);
      return response.data;
    },
    onError: () => {
      toast.error("Greška prilikom slanja marketing poruka!");
    },
    onSuccess: (res) => {
      toast.success(`Uspješno poslano!`);
    },
  });
};

// Send marketing with image file
export const useSendViberMarketingWithImage = () => {
  return useMutation<{ data: SendMarketingResponse; message: string }, Error, FormData>({
    mutationFn: async (formData: FormData) => {
      const response: AxiosResponse<{ data: SendMarketingResponse; message: string }> =
        await httpClient.post(sendViberMarketingWithImageEndpoint(), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      return response.data;
    },
    onError: () => {
      toast.error("Greška prilikom slanja marketing poruka!");
    },
    onSuccess: (res) => {
      toast.success(`Uspješno poslano ${res.data.sent} poruka!`);
    },
  });
};

// ── Message History ───────────────────────────────────────────────────────────

export interface ViberHistoryData {
  messages: ViberMessage[];
  stats: Record<string, number>;
}

export interface ViberHistoryMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ViberHistoryResponse {
  data: ViberHistoryData;
  meta: ViberHistoryMeta;
}

export interface ViberHistoryParams {
  page?: number;
  limit?: number;
  phoneNumber?: string;
  messageType?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  reservationId?: string;
  clientId?: string;
}

export const useGetViberMessageHistory = (
  params: ViberHistoryParams
): UseQueryResult<ViberHistoryResponse, unknown> => {
  return useQuery({
    queryKey: ["viberMessageHistory", params],
    queryFn: async () => {
      const response: AxiosResponse<{ statusCode: number; data: ViberHistoryData; meta: ViberHistoryMeta }> =
        await httpClient.get(
          getViberMessageHistoryEndpoint(
            params.page,
            params.limit,
            params.phoneNumber,
            params.messageType,
            params.status,
            params.dateFrom,
            params.dateTo,
            params.reservationId,
            params.clientId
          )
        );
      return { data: response.data.data, meta: response.data.meta };
    },
    retry: 1,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
  });
};

// ==================== Marketing / service counters ====================

export type ViberCountersPeriodType = "current" | "previous" | "custom";

export interface ViberCountersQuery {
  period: ViberCountersPeriodType;
  from?: string;
  to?: string;
}

export interface ViberServiceUsage {
  used: number;
  limit: number | null;
  remaining: number | null;
}

export interface ViberCounters {
  period: { type: ViberCountersPeriodType; from: string; to: string };
  marketing: number;
  /** Present only for SUPERADMIN / platform. */
  service?: number;
  total?: number;
  serviceUsage?: ViberServiceUsage;
}

export const fetchViberCounters = async (query: ViberCountersQuery): Promise<ViberCounters> => {
  const response: AxiosResponse<{ statusCode: number; message?: string; data: ViberCounters }> = await httpClient.get(
    getViberCountersEndpoint(),
    { params: query }
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data.message || "Brojači nisu dostupni");
  }
  return response.data.data;
};

export const saveViberServiceLimit = async (limit: number): Promise<ViberServiceUsage> => {
  const response: AxiosResponse<{ statusCode: number; message?: string; data: ViberServiceUsage }> = await httpClient.put(
    updateViberServiceLimitEndpoint(),
    { limit }
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response.data.message || "Limit nije sačuvan");
  }
  return response.data.data;
};
